import { useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import { client } from '../../../helpers/api-client';
import { useAppointment } from '../../../context/appointmentContext';
import { useResultData } from '../../../helpers/useResultData';
import { EyeResultsTable, SessionActions, useSessionUI } from '../../../ui';
import { ExamBody, useConfirmNavigation } from '../exams/common';

const ICNVisionResults = ({ localResults, setActiveTab, updateStep }) => {
  const [results, setResults] = useState({});
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();
  const [saving, setSaving] = useState(false);
  const { confirm, toast } = useSessionUI();
  const confirmNavigation = useConfirmNavigation();
  const {
    testData: { testResultsId },
  } = useTestContext();

  const getTestResults = (testId) => {
    client(`api/appointment-test-result/${testId}`).then(
      ({ result_data }) => {
        if (result_data) setResults(JSON.parse(result_data));
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const saveTestResults = async () => {
    const { corrected } = localResults;
    let testResultData = { leftEye: 'Eye not tested', rightEye: 'Eye not tested', corrected };
    if (localResults.testRightEye && localResults.rightEye) {
      testResultData['rightEye'] = localResults.rightEye;
    }

    if (localResults.testLeftEye && localResults.leftEye) {
      testResultData['leftEye'] = localResults.leftEye;
    }

    if (localResults.testBothEyes && localResults.bothEyes) {
      testResultData['bothEyes'] = localResults.bothEyes;
    }

    const sendTestResultData = {
      appointment_eye_test: localResults.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if (!(await confirm({ message: 'Are you sure you want to save the test results?' }))) return;
    setSaving(true);
    client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
      () => {
        setSaving(false);
        toast({ tone: 'success', title: 'Results saved', body: 'The Near Vision results were added to this appointment.' });
        setActiveTab('results');
      },
      (error) => {
        setSaving(false);
        console.error('Saving the test results failed', error);
        toast({ tone: 'error', title: 'The results could not be saved', body: error?.message || 'Please try again.' });
      },
    );
  };

  const restartTest = () => {
    confirmNavigation('restart').then(() => {
      clearAllResults();
      return updateStep('eyeSetup');
    });
  };

  useEffect(() => {
    if (!localResults) return getTestResults(testResultsId);
    if (localResults) return setResults(localResults);
  }, [testResultsId, localResults]);

  const binocular = Boolean(results.bothEyes);
  const eyes = binocular
    ? [{ label: 'Binocular', value: results.bothEyes || 'Eye not tested' }]
    : [
        { label: 'Right Eye', value: results.rightEye || 'Eye not tested' },
        { label: 'Left Eye', value: results.leftEye || 'Eye not tested' },
      ];

  return (
    <ExamBody>
      <div style={{ margin: 'auto 0', width: '100%' }} data-testid='near-vision-results'>
        <EyeResultsTable
          columns={eyes.map((e) => e.label)}
          style={binocular ? { maxWidth: 300 } : undefined}
          rows={[
            { cells: eyes.map((e) => (e.value.textSizeRead ? `Text size read: ${e.value.textSizeRead}` : 'Eye not tested')) },
            { tone: 'muted', cells: eyes.map((e) => <Magnification key={e.label} value={e.value.magnification} />) },
          ]}
        />
      </div>
      <SessionActions
        onBack={() => (localResults ? restartTest() : setActiveTab('results'))}
        primary={localResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: saveTestResults, disabled: saving } : undefined}
      />
    </ExamBody>
  );
};

// "(to read small print) Suggested magnification: +52d (14x)" - same wording as ICNResultsTable.
const Magnification = ({ value }) =>
  value ? (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, lineHeight: 1.35 }}>
      {value.startsWith('+') && <small style={{ fontSize: 11 }}>(to read small print)</small>}
      <span>Suggested magnification:</span>
      <b>{value}</b>
    </span>
  ) : (
    'Eye not tested'
  );

export default ICNVisionResults;
